import { InfoOutlined } from '@mui/icons-material';
import { Box, Chip, ToggleButton, ToggleButtonGroup, Tooltip, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
// eslint-disable-next-line no-restricted-imports -- pre-existing direct DataGridPro use; migrate to ServerDataGrid when touched
import { DataGridPro, useGridApiRef } from '@mui/x-data-grid-pro';
import { format, startOfDay } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import TimeRangeSelector from '@sophocles/components/TimeRangeSelector';
import DataGridToolbar, { DownloadButton } from '@sophocles/data-grids/components/toolbar';
import { REV } from '@sophocles/utils';
import { formatDateTime } from '@sophocles/utils/formatUtils';
import { getMeasurementTypeLabel, getUnitAbbreviation } from '@sophocles/utils/measurementUtils';

function MeasurementDataTable({
  dateTimeFormat,
  data,
  relays,
  relayInfo = {},
  displayRelays,
  sensors,
  timeZone,
  expectedReadings = {},
  displayUnit,
  onDisplayUnitChange,
  showUnitToggle = false,
  groupMode = 'all',
  onGroupModeChange,
  // Unit-type-specific columns
  isDC = false,
  rev,
  isVantage = false,
  // Tab / measurement type
  tab,
  onTabChange,
  measurementGroups = {},
  // Date range
  startDate,
  endDate,
  onDateRangeChange,
  dateRangeLoading = false,
  dateRangeExclude,
  defaultDatePreset,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const apiRef = useGridApiRef();

  const handleExport = useCallback(() => {
    apiRef.current.exportDataAsCsv({
      fileName: 'measurements',
      delimiter: ',',
      utf8WithBom: true,
      escapeFormulas: false,
    });
  }, [apiRef]);

  const exportButton = useMemo(
    () => <DownloadButton tooltip={t('general.export', { defaultValue: 'Export' })} onClick={handleExport} />,
    [handleExport, t],
  );

  const handleGroupModeChange = useCallback(
    val => {
      if (onGroupModeChange) onGroupModeChange(val);
    },
    [onGroupModeChange],
  );

  const sortedRelays = useMemo(
    () => (relays ? [...relays].sort((a, b) => a.get('relay_number') - b.get('relay_number')) : []),
    [relays],
  );

  // When displayRelays is provided, use it for paired relay grouping; otherwise fall back to one-per-relay
  const effectiveDisplayRelays = useMemo(() => {
    if (displayRelays && displayRelays.length > 0) return displayRelays;
    return sortedRelays.map(relay => {
      const rn = relay.get('relay_number');
      return {
        key: `relay_${rn}_power`,
        label: relay.get('name', `Relay ${rn}`),
        relayNumbers: [rn],
      };
    });
  }, [displayRelays, sortedRelays]);

  const sensorNumbers = useMemo(
    () =>
      sensors
        ? Array.from(
            new Set(
              (data || []).flatMap(row =>
                Object.keys(row.sensorMeasurements || {}).map(sensorNum => parseInt(sensorNum, 10)),
              ),
            ),
          ).sort((a, b) => a - b)
        : [],
    [sensors, data],
  );

  // Per-display-relay expected watts — primary relay's loads only
  const relayExpected = useMemo(() => {
    const map = {};
    effectiveDisplayRelays.forEach(dr => {
      const loads = relayInfo[dr.relayNumbers[0]]?.loads || [];
      const total = loads.reduce((s, l) => s + l.fixtureWatts * l.fixtureCount, 0);
      if (total > 0) map[dr.key] = total;
    });
    return map;
  }, [effectiveDisplayRelays, relayInfo]);

  const columns = useMemo(() => {
    const timestampCol = {
      field: 'timestamp',
      headerName: groupMode === 'byDay' ? t('general.date') : t('general.timestamp'),
      width: 200,
      filterable: false,
      disableColumnMenu: true,
      valueFormatter: (value, row) => {
        if (row?._aggregation) return row._aggregation;
        if (!value) return '—';
        return groupMode === 'byDay'
          ? format(new Date(value), 'yyyy-MM-dd')
          : formatDateTime(toZonedTime(value, timeZone || 'UTC'), dateTimeFormat);
      },
    };

    if (sensors) {
      return [
        timestampCol,
        ...sensorNumbers.map(sensorNum => ({
          field: `sensor_${sensorNum}`,
          headerName: `${t('measurements.sensor')} ${sensorNum}`,
          width: 150,
          type: 'number',
          sortable: false,
          filterable: false,
          disableColumnMenu: true,
          valueGetter: (_value, row) => {
            if (groupMode === 'byDay') return row[`sensor_${sensorNum}`] ?? null;
            const m = row.sensorMeasurements?.[sensorNum];
            return m ? (typeof m.value === 'number' ? m.value : parseFloat(m.value) || null) : null;
          },
          valueFormatter: (value, row) => {
            if (value == null) return '—';
            if (groupMode === 'byDay') {
              const unit = row?.[`sensor_${sensorNum}_unit`] || '';
              return `${Math.round(value * 10) / 10} ${unit}`.trim();
            }
            const m = row?.sensorMeasurements?.[sensorNum];
            const unit = m ? getUnitAbbreviation(m.unit) : '';
            return `${Math.round(value * 10) / 10} ${unit}`.trim();
          },
        })),
      ];
    }

    const relayCols = effectiveDisplayRelays.map(dr => {
      const loadNames = dr.relayNumbers.flatMap(rn => (relayInfo[rn]?.loads || []).map(l => l.name)).filter(Boolean);

      const expected = relayExpected[dr.key];
      const powerActive = dr.relayNumbers.every(rn => relayInfo[rn]?.powerMeasurementActive !== false);
      const primaryRelayNumber = dr.relayNumbers[0];

      return {
        field: dr.key,
        headerName: dr.label,
        description: loadNames.length > 0 ? loadNames.join(', ') : undefined,
        width: 150,
        type: 'number',
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        cellClassName: params => {
          if (
            !powerActive ||
            displayUnit === 'current' ||
            params.row?._aggregation ||
            !expected ||
            params.value == null
          )
            return '';
          // Red text when reading deviates from expected by more than half of smallest individual fixture wattage
          const allLoads = dr.relayNumbers.flatMap(rn => relayInfo[rn]?.loads || []);
          const fixtureWatts = allLoads.find(l => l.fixtureWatts > 0)?.fixtureWatts || 0;
          if (fixtureWatts > 0) {
            const diff = Math.abs(expected - params.value);
            if (diff > fixtureWatts / 2) return 'cell-text-red';
          }
          return '';
        },
        valueGetter: (_value, row) => {
          if (row?._aggregation) return row[dr.key] ?? null;
          if (groupMode === 'byDay') return row[dr.key] ?? null;
          // Use primary relay value only (voltage already represents combined circuit)
          const m = row.relayMeasurements?.[primaryRelayNumber];
          if (!m) return null;
          const val = typeof m.value === 'number' ? m.value : parseFloat(m.value) || null;
          return val;
        },
        valueFormatter: (value, row) => {
          if (value == null) return '—';
          // Use 3 decimals for current (amps), 2 decimals for power/voltage
          const formatValue = (val, unit) => {
            const isCurrentUnit = unit === 'A' || unit === 'amps';
            return isCurrentUnit ? val.toFixed(3) : val.toFixed(2).replace(/\.00$/, '');
          };
          if (row?._aggregation) {
            const unitLabel = displayUnit === 'current' ? 'A' : 'W';
            return `${formatValue(value, unitLabel)} ${unitLabel}`;
          }
          if (groupMode === 'byDay') {
            const unit = row?.[`${dr.key}_unit`] || '';
            const target = row?.[`${dr.key}_target`];
            const display = `${formatValue(value, unit)} ${unit}`.trim();
            return target != null ? `${display} (${target}V)` : display;
          }
          // Use primary relay for unit/voltage annotation
          const m = row?.relayMeasurements?.[primaryRelayNumber];
          const unit = m ? getUnitAbbreviation(m.unit) : '';
          const display = `${formatValue(value, unit)} ${unit}`.trim();
          return m?.target_voltage != null ? `${display} (${m.target_voltage}V)` : display;
        },
      };
    });

    // DC unit-specific columns: input voltage and temperature
    // REV A has a single input voltage (battery_voltage), REV B has two (input_voltage_1, input_voltage_2)
    const dcCols = !isDC
      ? []
      : [
          ...(rev === REV.A
            ? [
                {
                  field: 'battery_voltage',
                  headerName: t('measurements.inputVoltage'),
                  width: 140,
                  type: 'number',
                  sortable: false,
                  filterable: false,
                  disableColumnMenu: true,
                  valueGetter: (_value, row) => row?.battery_voltage ?? null,
                  valueFormatter: value => (value != null ? `${value.toFixed(2).replace(/\.00$/, '')} V` : '—'),
                },
              ]
            : [
                {
                  field: 'input_voltage_1',
                  headerName: `${t('measurements.inputVoltage')} 1`,
                  width: 140,
                  type: 'number',
                  sortable: false,
                  filterable: false,
                  disableColumnMenu: true,
                  valueGetter: (_value, row) => row?.input_voltage_1 ?? null,
                  valueFormatter: value => (value != null ? `${value.toFixed(2).replace(/\.00$/, '')} V` : '—'),
                },
                {
                  field: 'input_voltage_2',
                  headerName: `${t('measurements.inputVoltage')} 2`,
                  width: 140,
                  type: 'number',
                  sortable: false,
                  filterable: false,
                  disableColumnMenu: true,
                  valueGetter: (_value, row) => row?.input_voltage_2 ?? null,
                  valueFormatter: value => (value != null ? `${value.toFixed(2).replace(/\.00$/, '')} V` : '—'),
                },
              ]),
          {
            field: 'thermistor_temperature',
            headerName: t('measurements.temperature'),
            width: 140,
            type: 'number',
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            valueGetter: (_value, row) => row?.thermistor_temperature ?? null,
            valueFormatter: value => (value != null ? `${value.toFixed(2).replace(/\.00$/, '')} °C` : '—'),
          },
        ];

    // Vantage unit-specific columns: cpu input voltage and battery voltage
    const vantageCols = !isVantage
      ? []
      : [
          {
            field: 'cpu_input_voltage',
            headerName: `${t('measurements.voltage')} 1`,
            width: 140,
            type: 'number',
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            valueGetter: (_value, row) => row?.cpu_input_voltage ?? null,
            valueFormatter: value => (value != null ? `${value.toFixed(2).replace(/\.00$/, '')} V` : '—'),
          },
          {
            field: 'battery_voltage',
            headerName: `${t('measurements.voltage')} 2`,
            width: 140,
            type: 'number',
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            valueGetter: (_value, row) => row?.battery_voltage ?? null,
            valueFormatter: value => (value != null ? `${value.toFixed(2).replace(/\.00$/, '')} V` : '—'),
          },
        ];

    return [timestampCol, ...relayCols, ...dcCols, ...vantageCols];
  }, [
    sensors,
    sensorNumbers,
    effectiveDisplayRelays,
    relayInfo,
    relayExpected,
    dateTimeFormat,
    timeZone,
    t,
    groupMode,
    displayUnit,
    isDC,
    rev,
    isVantage,
  ]);

  const columnGroupingModel = useMemo(() => {
    if (sensors) return [];
    return effectiveDisplayRelays
      .map(dr => {
        const loadNames = dr.relayNumbers.flatMap(rn => (relayInfo[rn]?.loads || []).map(l => l.name)).filter(Boolean);
        if (loadNames.length === 0) return null;
        return {
          groupId: `${dr.key}_group`,
          headerName: loadNames.join(', '),
          children: [{ field: dr.key }],
        };
      })
      .filter(Boolean);
  }, [sensors, effectiveDisplayRelays, relayInfo]);

  const rows = useMemo(() => {
    const sourceData = data || [];

    if (groupMode === 'byDay') {
      const dayGroups = {};

      sourceData.forEach(row => {
        const ts = row.timestamp ? new Date(row.timestamp) : null;
        if (!ts) return;
        const zoned = toZonedTime(ts, timeZone || 'UTC');
        const dayKey = format(startOfDay(zoned), 'yyyy-MM-dd');

        if (!dayGroups[dayKey]) {
          dayGroups[dayKey] = { timestamp: startOfDay(zoned) };
        }
        const group = dayGroups[dayKey];

        if (sensors) {
          sensorNumbers.forEach(sensorNum => {
            const m = row.sensorMeasurements?.[sensorNum];
            if (!m) return;
            const val = typeof m.value === 'number' ? m.value : parseFloat(m.value);
            if (isNaN(val)) return;
            const field = `sensor_${sensorNum}`;
            group[field] = group[field] != null ? Math.max(group[field], val) : val;
            group[`${field}_unit`] = getUnitAbbreviation(m.unit);
          });
        } else {
          effectiveDisplayRelays.forEach(dr => {
            // Use primary relay value only (voltage already represents combined circuit)
            const m = row.relayMeasurements?.[dr.relayNumbers[0]];
            if (!m) return;
            const val = typeof m.value === 'number' ? m.value : parseFloat(m.value);
            if (isNaN(val)) return;
            const field = dr.key;
            group[field] = group[field] != null ? Math.max(group[field], val) : val;
            group[`${field}_unit`] = getUnitAbbreviation(m.unit);
            if (m.target_voltage != null) group[`${field}_target`] = m.target_voltage;
          });

          // Aggregate DC/Vantage auxiliary fields (max for the day)
          [
            'battery_voltage',
            'input_voltage_1',
            'input_voltage_2',
            'cpu_input_voltage',
            'thermistor_temperature',
          ].forEach(field => {
            const val = row[field];
            if (val != null && !isNaN(val)) {
              group[field] = group[field] != null ? Math.max(group[field], val) : val;
            }
          });
        }
      });

      return Object.values(dayGroups).map((row, i) => ({ id: i, ...row }));
    }

    return sourceData.map((row, i) => ({ id: i, ...row }));
  }, [data, groupMode, sensors, sensorNumbers, effectiveDisplayRelays, timeZone]);

  const pinnedRows = useMemo(() => {
    if (sensors || rows.length === 0) return {};

    const maxRow = { id: '__max__', timestamp: null, _aggregation: t('general.max') };
    const minRow = { id: '__min__', timestamp: null, _aggregation: t('general.min') };

    effectiveDisplayRelays.forEach(dr => {
      const field = dr.key;
      const values = rows
        .map(r => {
          if (groupMode === 'byDay') return r[field];
          // Use primary relay value only (voltage already represents combined circuit)
          const m = r.relayMeasurements?.[dr.relayNumbers[0]];
          if (!m) return null;
          const val = typeof m.value === 'number' ? m.value : parseFloat(m.value) || null;
          return val;
        })
        .filter(v => v != null);

      if (values.length > 0) {
        maxRow[field] = Math.max(...values);
        minRow[field] = Math.min(...values);
      }
    });

    return { bottom: [maxRow, minRow] };
  }, [rows, sensors, effectiveDisplayRelays, groupMode, t]);

  const listViewColumn = useMemo(
    () => ({
      field: 'listColumn',
      renderCell: params => {
        const { row } = params;

        let timeDisplay;
        if (row._aggregation) {
          timeDisplay = row._aggregation;
        } else if (!row.timestamp) {
          timeDisplay = '—';
        } else if (groupMode === 'byDay') {
          timeDisplay = format(new Date(row.timestamp), 'yyyy-MM-dd');
        } else {
          timeDisplay = formatDateTime(toZonedTime(row.timestamp, timeZone || 'UTC'), dateTimeFormat);
        }

        const entries = [];

        if (sensors) {
          sensorNumbers.forEach(sensorNum => {
            const field = `sensor_${sensorNum}`;
            let val;
            if (groupMode === 'byDay') {
              val = row[field];
            } else {
              const m = row.sensorMeasurements?.[sensorNum];
              val = m ? (typeof m.value === 'number' ? m.value : parseFloat(m.value) || null) : null;
            }
            if (val == null) return;
            const unit =
              groupMode === 'byDay'
                ? row[`${field}_unit`] || ''
                : getUnitAbbreviation(row.sensorMeasurements?.[sensorNum]?.unit);
            entries.push({
              label: `${t('measurements.sensor')} ${sensorNum}`,
              value: `${Math.round(val * 10) / 10} ${unit}`.trim(),
            });
          });
        } else {
          effectiveDisplayRelays.forEach(dr => {
            const field = dr.key;
            let val;
            if (row._aggregation || groupMode === 'byDay') {
              val = row[field];
            } else {
              // Use primary relay value only (voltage already represents combined circuit)
              const m = row.relayMeasurements?.[dr.relayNumbers[0]];
              val = m ? (typeof m.value === 'number' ? m.value : parseFloat(m.value) || null) : null;
            }
            if (val == null) return;

            let isRed = false;
            const powerActive = dr.relayNumbers.every(rn => relayInfo[rn]?.powerMeasurementActive !== false);
            if (powerActive && displayUnit !== 'current' && !row._aggregation) {
              const expected = relayExpected[field];
              if (expected) {
                const allLoads = dr.relayNumbers.flatMap(rn => relayInfo[rn]?.loads || []);
                const fixtureWatts = allLoads.find(l => l.fixtureWatts > 0)?.fixtureWatts || 0;
                if (fixtureWatts > 0) {
                  const diff = Math.abs(expected - val);
                  if (diff > fixtureWatts / 2) isRed = true;
                }
              }
            }

            // Use 3 decimals for current, 2 decimals for power/voltage
            const formattedVal = displayUnit === 'current' ? val.toFixed(3) : val.toFixed(2).replace(/\.00$/, '');
            entries.push({
              label: dr.label,
              value: `${formattedVal}`,
              isRed,
            });
          });
        }

        return (
          <Box sx={{ py: 1, px: 0.5, width: '100%' }}>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
              {timeDisplay}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {entries.map((entry, i) => (
                <Chip
                  key={i}
                  label={`${entry.label}: ${entry.value}`}
                  size="small"
                  variant="outlined"
                  sx={
                    entry.isRed ? { color: theme.palette.error.main, borderColor: theme.palette.error.main } : undefined
                  }
                />
              ))}
            </Box>
          </Box>
        );
      },
    }),
    [
      sensors,
      sensorNumbers,
      effectiveDisplayRelays,
      relayInfo,
      relayExpected,
      displayUnit,
      groupMode,
      timeZone,
      dateTimeFormat,
      t,
      theme,
    ],
  );

  const sortedGroupKeys = useMemo(() => {
    const order = ['peak', 'live', 'average'];
    return Object.keys(measurementGroups).sort((a, b) => {
      const ai = order.indexOf(a);
      const bi = order.indexOf(b);
      if (ai !== -1 && bi !== -1) return ai - bi;
      if (ai !== -1) return -1;
      if (bi !== -1) return 1;
      return a.localeCompare(b);
    });
  }, [measurementGroups]);

  const toolbarMainControls = useMemo(
    () => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {/* Measurement type toggle (replaces page-level tabs) */}
        {sortedGroupKeys.length > 1 && onTabChange && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <ToggleButtonGroup size="small" value={tab} exclusive onChange={(_e, val) => val && onTabChange(null, val)}>
              {sortedGroupKeys.map(groupKey => (
                <ToggleButton key={groupKey} value={groupKey} disabled={dateRangeLoading}>
                  {getMeasurementTypeLabel(measurementGroups[groupKey]?.displayType, t)}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            {sortedGroupKeys.includes('peak') && sortedGroupKeys.includes('live') && (
              <Tooltip
                title={`${t('measurements.types.peak', 'Peak')}: ${t('measurements.peakTooltip', "Highest power reading recorded since the device's last scheduled check-in.")} ${t('measurements.types.live', 'Live')}: ${t('measurements.liveTooltip', 'On-demand point-in-time reading requested directly from the device.')}`}
              >
                <InfoOutlined sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
              </Tooltip>
            )}
          </Box>
        )}

        {/* Power / Current unit toggle */}
        {showUnitToggle && onDisplayUnitChange && (
          <ToggleButtonGroup
            size="small"
            value={displayUnit}
            exclusive
            onChange={(_e, val) => val && onDisplayUnitChange(val)}
          >
            <ToggleButton value="power">{t('measurements.units.power')}</ToggleButton>
            <ToggleButton value="current">{t('measurements.units.current')}</ToggleButton>
          </ToggleButtonGroup>
        )}

        {/* Date range selector */}
        {onDateRangeChange && (
          <TimeRangeSelector
            startDate={startDate}
            endDate={endDate}
            onChange={onDateRangeChange}
            disabled={dateRangeLoading}
            size="sm"
            exclude={dateRangeExclude}
            defaultPreset={defaultDatePreset}
          />
        )}
      </Box>
    ),
    [
      tab,
      onTabChange,
      sortedGroupKeys,
      measurementGroups,
      showUnitToggle,
      displayUnit,
      onDisplayUnitChange,
      startDate,
      endDate,
      onDateRangeChange,
      dateRangeLoading,
      dateRangeExclude,
      defaultDatePreset,
      t,
    ],
  );

  if (!data || data.length === 0) {
    return (
      <Box>
        {/* Always render toolbar so user can change date range / tab even when empty */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1, px: 0.5 }}>{toolbarMainControls}</Box>
        <Box sx={{ mt: 2, p: 2, bgcolor: 'var(--surface-secondary)', borderRadius: 1 }}>
          <Typography variant="h6" gutterBottom>
            {t('measurements.measurementData')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('measurements.noMeasurementsInRange')}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, mt: 2 }}>
      <DataGridToolbar
        apiRef={apiRef}
        showQuickFilter={false}
        toolbarContent={toolbarMainControls}
        toolbarActions={exportButton}
      />
      <DataGridPro
        apiRef={apiRef}
        rows={rows}
        columns={columns}
        columnGroupingModel={isMobile ? undefined : columnGroupingModel}
        pinnedRows={pinnedRows}
        listView={isMobile}
        listViewColumn={listViewColumn}
        getRowHeight={() => 'auto'}
        getRowClassName={params => (params.row?._aggregation ? 'agg-row' : '')}
        disableDensitySelector
        disableColumnFilter
        disableColumnSelector
        clipboardCopyCellDelimiter={'\t'}
        initialState={{
          sorting: { sortModel: [{ field: 'timestamp', sort: 'desc' }] },
          pinnedColumns: { left: ['timestamp'] },
        }}
        density="compact"
        disableRowSelectionOnClick
        hideFooter
        sx={{
          maxHeight: 'max(300px, calc(100vh - 600px))',
          '& .MuiDataGrid-pinnedRows': {
            bgcolor: 'var(--grid-header-bg)',
            borderTop: 1,
            borderColor: 'var(--grid-border)',
          },
          '& .agg-row': { fontWeight: 'bold' },
          '& .cell-text-red': { color: theme => theme.palette.error.main },
        }}
      />
    </Box>
  );
}

export default MeasurementDataTable;
